import { useContext, useState, useMemo } from "react";
import { FaHandPointer } from "react-icons/fa";
import { ServicesContext } from "../../context/ServicesDataProvider";
import mousepointer from "../../assets/images/mouse_hand.png";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] overflow-auto relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-xl"
        >
          &times;
        </button>
        {children}
      </div>
    </div>
  );
};

// Helper: Approximate invoice numbers with ₹
const formatInvoice = (num) => {
  if (!num && num !== 0) return "₹ 0";
  if (num >= 10000000) return `₹ ${(num / 10000000).toFixed(1)} Cr`;
  if (num >= 100000) return `₹ ${(num / 100000).toFixed(1)} L`;
  if (num >= 1000) return `₹ ${(num / 1000).toFixed(1)} K`;
  return `₹ ${num.toLocaleString("en-IN")}`;
};

// Helper: Round TAT hours
const formatTAT = (num) => {
  if (!num && num !== 0) return 0;
  return Math.round(num);
};

export default function LastServiceDetails() {
  const { lastThreeDetailBoxData, lastThreeDetailPopupData } =
    useContext(ServicesContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const columns = useMemo(
    () => [
      { header: "Order Number", accessorKey: "_source.ORDER_NUM" },
      { header: "Workshop Name", accessorKey: "_source.DLR_NAME" },
      {
        header: "Vehicle Reg. No.",
        accessorKey: "_source.VEHICLE_REGISTRATION_NUMBER",
      },
      { header: "Service Type", accessorFn: () => "N/A", id: "serviceType" },
      {
        header: "Bill Amount",
        accessorKey: "_source.Bill_Amount",
        cell: (info) => formatInvoice(info.getValue()),
      },
      {
        header: "TAT (Hrs)",
        accessorFn: (row) => row.fields?.TAT_hrs?.[0] || 0,
        id: "tatHrs",
        cell: (info) => `${formatTAT(info.getValue())} Hrs`,
      },
      { header: "Service Date", accessorFn: () => "N/A", id: "serviceDate" },
    ],
    []
  );

  const data = useMemo(
    () => lastThreeDetailPopupData || [],
    [lastThreeDetailPopupData]
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full">
      <div className="bg-[#2374BF] flex justify-between items-center text-white p-1 pl-2 font-semibold text-md rounded-t-lg">
        <h2> Last 3 Service Details</h2>
        <div
          className="mt-2 mb-4 flex mr-8 justify-end text-gray-600 cursor-pointer hover:text-blue-600"
          onClick={handleOpenModal}
        >
          <span className="mr-2  text-white font-semibold">More Details</span>
          <img src={mousepointer} />
        </div>{" "}
      </div>

      {/* Timeline */}
      <div className="relative mt-8 flex flex-col items-center">
        <div className="absolute left-1/2 top-0 transform -translate-x-1/2 h-full border-l-2 border-dashed border-gray-400"></div>

        {lastThreeDetailBoxData?.map((item, index) => {
          const formattedDate = item?.key?.last_service_Dt
            ? item.key.last_service_Dt
                .toString()
                .replace(/^(\d{4})(\d{2})(\d{2})$/, "$3-$2-$1") // DD-MM-YYYY
            : "";

          return (
            <ServiceCard
              key={index}
              position={index % 2 === 0 ? "left" : "right"}
              date={formattedDate}
              tat={`${formatTAT(item?.Avg_TAT_Hrs?.value)} Hrs`}
              invoice={formatInvoice(item?.invoice_amount?.value)}
            />
          );
        })}
      </div>

      {/* More Details Button */}

      {/* Modal Table */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <h3 className="text-xl font-bold mb-4">All Service Details</h3>
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-gray-300 rounded">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-200 sticky top-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="py-2 px-4 border-b border-gray-300 text-left text-sm font-semibold text-gray-700"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="py-2 px-4 border-b border-gray-300 text-sm text-gray-800"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length === 0 && (
            <p className="text-center py-4 text-gray-600">
              No service details available.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

const ServiceCard = ({ position, date, tat, invoice }) => (
  <div
    className={`relative flex w-full mb-10 ${
      position === "left" ? "justify-start" : "justify-end"
    }`}
  >
    <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center">
      <div className="w-4 h-4 bg-[#2374BF] rounded-full border-2 border-white z-20"></div>
    </div>

    <div
      className={`bg-gray-100 p-4 rounded-lg shadow-md w-60 relative ${
        position === "left" ? "ml-8" : "mr-8"
      }`}
    >
      <p className="font-semibold text-center">{date}</p>
      <hr className="my-2" />
      <p className="text-gray-600">
        TAT: <span className="font-medium">{tat}</span>
      </p>
      <p className="text-gray-600">
        Invoice: <span className="font-medium">{invoice}</span>
      </p>
    </div>
  </div>
);
